import React, { useState, useEffect, useMemo } from 'react';
import RecebidosEmail from './controladoria/RecebidosEmail';
import { db, getCollectionName } from './firebase';
import {
  collection, query, where, onSnapshot, orderBy, doc, getDocs,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
} from './firestore-compat';

// ── Constantes ────────────────────────────────────────────────────────────────
const ANDAMENTO_OPTS = ['Não iniciado','Em andamento','Concluído'];
const ANDAMENTO_COLOR = { 'Não iniciado': '#64748b', 'Em andamento': '#a16207', 'Concluído': '#15803d' };

const P = {
  bg:'#f8fafc', white:'#fff', border:'#e2e8f0', text:'#1e293b',
  muted:'#64748b', primary:'#6366f1', green:'#22c55e',
  red:'#ef4444', amber:'#f59e0b', blue:'#3b82f6', gray:'#94a3b8',
};

const TABS = [
  { k:'notas',     l:'💳 Pagamento de Notas' },
  { k:'emails',    l:'📥 Recebidos por e-mail' },
  { k:'contratos', l:'📋 Contratos/Fornecedores' },
  { k:'calendario',l:'🗓️ Calendário' },
  { k:'km',        l:'🚗 Controle de KM' },
  { k:'multas',    l:'🚨 Controle de Multas' },
];

const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DIA_SEMANA_PT = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s || typeof s !== 'string') return '—';
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  return `${match[3]}/${match[2]}/${match[1]}`;
}
function toSortDate(val) {
  if (!val || typeof val !== 'string') return '';
  const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
function fmtMoeda(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
}
function diasAte(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00');
  if (isNaN(d)) return null;
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  return Math.ceil((d - hoje) / 86400000);
}
function prazoLabel(dias) {
  if (dias === null) return '—';
  if (dias < 0) return `Atrasado ${Math.abs(dias)}d`;
  if (dias === 0) return 'Hoje';
  return `Faltam ${dias}d`;
}
function prazoCor(dias) {
  if (dias === null) return P.muted;
  if (dias < 0) return P.red;
  if (dias <= 3) return P.amber;
  return P.green;
}
function dstr(d) {
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
}
function isDiaUtil(d) {
  const dow = d.getDay();
  return dow !== 0 && dow !== 6; // não conta feriados, só fim de semana
}
/** N-ésimo dia útil do mês (1 = primeiro dia útil). Se passar do fim do mês, cai no último dia. */
function nthDiaUtil(ano, mesIdx, n) {
  const d = new Date(ano, mesIdx, 1);
  let count = 0;
  while (d.getMonth() === mesIdx) {
    if (isDiaUtil(d)) { count++; if (count === n) return new Date(d); }
    d.setDate(d.getDate() + 1);
  }
  return new Date(ano, mesIdx + 1, 0); // não achou — usa o último dia do mês
}
/** Dia fixo do mês (ex.: dia 31 num mês com 30 dias vira o último dia do mês). */
function diaFixoDoMes(ano, mesIdx, dia) {
  const ultimoDia = new Date(ano, mesIdx + 1, 0).getDate();
  return new Date(ano, mesIdx, Math.min(dia, ultimoDia));
}
function calcularDataRegra(ano, mesIdx, tipo, valor) {
  return tipo === 'dia_util' ? nthDiaUtil(ano, mesIdx, Number(valor) || 1) : diaFixoDoMes(ano, mesIdx, Number(valor) || 1);
}
/** Registro pendente (status != Concluído) mais próximo pro campo de data indicado. */
function proximoDe(data, campoData, campoStatus) {
  const candidatos = data
    .filter(r => r[campoData] && r[campoStatus] !== 'Concluído')
    .map(r => ({ ...r, dias: diasAte(r[campoData]) }))
    .filter(r => r.dias !== null)
    .sort((a,b) => a.dias - b.dias);
  return candidatos[0] || null;
}

// ── Mini-componentes ──────────────────────────────────────────────────────────
function KpiCard({ label, value, color, onClick, sub }) {
  return (
    <div onClick={onClick} style={{ background:P.white, border:`1px solid ${P.border}`, borderRadius:10,
      padding:'12px 18px', minWidth:150, flex:1, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize:'0.68rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5 }}>{label}</div>
      <div style={{ fontSize:'1.5rem', fontWeight:800, color, lineHeight:1.2, margin:'4px 0 0' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.68rem', color:P.muted, marginTop:2 }}>{sub}</div>}
    </div>
  );
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:P.white, borderRadius:14, width:'100%', maxWidth:wide?800:560,
        maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'16px 22px', borderBottom:`1px solid ${P.border}`,
          position:'sticky', top:0, background:P.white, zIndex:1 }}>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:P.text }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.3rem', cursor:'pointer', color:P.muted }}>✕</button>
        </div>
        <div style={{ padding:'20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:'0.71rem', fontWeight:700, color:P.muted,
        textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
        {label}{required && <span style={{ color:P.red }}> *</span>}
      </label>
      {children}
    </div>
  );
}
const inp = {
  width:'100%', padding:'8px 11px', border:`1px solid ${P.border}`, borderRadius:8,
  fontSize:'0.85rem', background:P.white, color:P.text, boxSizing:'border-box', outline:'none',
};
function Sel({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function Btn({ children, onClick, disabled, color = P.primary, small }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background:disabled?P.border:color, color:'#fff', border:'none',
        borderRadius:8, padding:small?'5px 12px':'8px 18px',
        fontWeight:700, fontSize:small?'0.78rem':'0.84rem',
        cursor:disabled?'default':'pointer', opacity:disabled?.6:1 }}>
      {children}
    </button>
  );
}
function GhostBtn({ children, onClick, small }) {
  return (
    <button onClick={onClick}
      style={{ background:'none', border:`1px solid ${P.border}`, color:P.muted,
        borderRadius:8, padding:small?'4px 10px':'7px 14px',
        fontWeight:600, fontSize:small?'0.78rem':'0.82rem', cursor:'pointer' }}>
      {children}
    </button>
  );
}
function TH({ children }) {
  return <th style={{ padding:'9px 12px', textAlign:'left', fontSize:'0.68rem', fontWeight:700,
    color:P.muted, textTransform:'uppercase', letterSpacing:.5, borderBottom:`1px solid ${P.border}`,
    whiteSpace:'nowrap' }}>{children}</th>;
}
function DelModal({ onClose, onConfirm }) {
  return (
    <Modal title="Confirmar exclusão" onClose={onClose}>
      <p style={{ color:P.text, marginBottom:20 }}>Deseja excluir este registro? Esta ação não pode ser desfeita.</p>
      <div style={{ display:'flex', gap:10 }}>
        <Btn color={P.red} onClick={onConfirm}>Excluir</Btn>
        <GhostBtn onClick={onClose}>Cancelar</GhostBtn>
      </div>
    </Modal>
  );
}

// ── Aba 1: Pagamento de Notas ────────────────────────────────────────────────────
const EMPTY_NOTA = {
  fornecedor:'', filial:'',
  data_previa:'',     status_previa:'Não iniciado',
  data_envio:'',      status_envio:'Não iniciado',
  data_nota:'',       status_nota:'Não iniciado',
  data_pagamento:'',  status_pagamento:'Não iniciado',
  validacao_itworks:'Pendente',
  observacoes:'',
  contrato_id:'', origem:'manual',
};

function EtapaCell({ data, status, onChangeStatus }) {
  return (
    <div>
      <div style={{ color:P.text, fontSize:'0.75rem', whiteSpace:'nowrap' }}>{fmtDate(data)}</div>
      <select value={status||'Não iniciado'} onChange={e=>onChangeStatus(e.target.value)}
        style={{ marginTop:2, width:'100%', fontSize:'0.68rem', border:`1px solid ${P.border}`, borderRadius:6,
          padding:'2px 4px', color:ANDAMENTO_COLOR[status||'Não iniciado'], fontWeight:700, background:P.white, cursor:'pointer' }}>
        {ANDAMENTO_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function TabNotas({ data, col, filiais = [] }) {
  const [search,      setSearch]      = useState('');
  const [filFilial,   setFilFilial]   = useState('');
  const [modal,       setModal]       = useState(false);
  const [edit,        setEdit]        = useState(null);
  const [form,        setForm]        = useState(EMPTY_NOTA);
  const [saving,      setSaving]      = useState(false);
  const [delId,       setDelId]       = useState(null);

  const openNew  = () => { setForm(EMPTY_NOTA); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...EMPTY_NOTA, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.fornecedor.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db,col,edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db,col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const quickField = async (r, field, value) => {
    await updateDoc(doc(db, col, r.id), { [field]: value, updatedAt: serverTimestamp() });
  };

  const rows = useMemo(() => {
    let r = data;
    if (search) r = r.filter(x => (x.fornecedor||'').toLowerCase().includes(search.toLowerCase()));
    if (filFilial) r = r.filter(x => x.filial === filFilial);
    return [...r].sort((a,b) => (toSortDate(b.data_previa)||'').localeCompare(toSortDate(a.data_previa)||''));
  }, [data, search, filFilial]);

  const kpis = useMemo(() => ({
    total: data.length,
  }), [data]);

  // Próxima prévia / envio / vencimento pendentes (exclui o que já foi concluído),
  // ordenados pelo mais urgente primeiro (atrasado conta como mais urgente ainda)
  const proximaPrevia     = useMemo(() => proximoDe(data, 'data_previa','status_previa'), [data]);
  const proximoEnvio      = useMemo(() => proximoDe(data, 'data_envio','status_envio'),   [data]);
  const proximoVencimento = useMemo(() => proximoDe(data, 'data_nota','status_nota'),     [data]);

  // Listagem de quem está mais próximo — junta envio e vencimento pendentes num só
  // ranking, sempre visível (não só o card do mais urgente)
  const maisProximos = useMemo(() => {
    const eventos = [];
    data.forEach(r => {
      if (r.data_envio && r.status_envio !== 'Concluído') {
        const dias = diasAte(r.data_envio);
        if (dias !== null) eventos.push({ id:r.id+'_envio', fornecedor:r.fornecedor, filial:r.filial, tipo:'Envio p/ Controle de Notas', data:r.data_envio, dias });
      }
      if (r.data_nota && r.status_nota !== 'Concluído') {
        const dias = diasAte(r.data_nota);
        if (dias !== null) eventos.push({ id:r.id+'_venc', fornecedor:r.fornecedor, filial:r.filial, tipo:'Vencimento (Pagamento)', data:r.data_nota, dias });
      }
    });
    return eventos.sort((a,b) => a.dias - b.dias).slice(0, 8);
  }, [data]);

  const hasF = search || filFilial;
  const clrF = () => { setSearch(''); setFilFilial(''); };

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <KpiCard label="Total de notas"                value={kpis.total}             color={P.muted} />
        <KpiCard label="⏰ Próxima Prévia"
          value={proximaPrevia ? prazoLabel(proximaPrevia.dias) : '—'}
          color={proximaPrevia ? prazoCor(proximaPrevia.dias) : P.muted}
          sub={proximaPrevia ? `${proximaPrevia.fornecedor} · ${fmtDate(proximaPrevia.data_previa)}` : 'Nenhuma prévia pendente'} />
        <KpiCard label="⏰ Próximo Envio"
          value={proximoEnvio ? prazoLabel(proximoEnvio.dias) : '—'}
          color={proximoEnvio ? prazoCor(proximoEnvio.dias) : P.muted}
          sub={proximoEnvio ? `${proximoEnvio.fornecedor} · ${fmtDate(proximoEnvio.data_envio)}` : 'Nenhum envio pendente'} />
        <KpiCard label="⏰ Próximo Vencimento"
          value={proximoVencimento ? prazoLabel(proximoVencimento.dias) : '—'}
          color={proximoVencimento ? prazoCor(proximoVencimento.dias) : P.muted}
          sub={proximoVencimento ? `${proximoVencimento.fornecedor} · ${fmtDate(proximoVencimento.data_nota)}` : 'Nenhum vencimento pendente'} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'2.2fr 1fr', gap:16, alignItems:'flex-start' }}>
        <div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
            <Btn onClick={openNew}>+ Nova Nota</Btn>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar fornecedor…"
              style={{ ...inp, maxWidth:220, flex:1 }} />
            <select value={filFilial} onChange={e=>setFilFilial(e.target.value)} style={{ ...inp, maxWidth:160 }}>
              <option value="">Todas as filiais</option>
              {filiais.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
            </select>
            {hasF && <GhostBtn onClick={clrF}>✕ Limpar</GhostBtn>}
            <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{rows.length} registro{rows.length!==1?'s':''}</span>
          </div>

          <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflowX:'auto' }}>
            <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.75rem', minWidth:1000 }}>
              <thead><tr>
                <TH>Fornecedor</TH><TH>Filial</TH><TH>Data Prévia</TH><TH>Data Envio para Controle de Notas</TH><TH>Vencimento</TH>
                <TH>Ações</TH>
              </tr></thead>
              <tbody>
                {rows.length===0 && <tr><td colSpan={6} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
                {rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9', background:i%2===0?'transparent':'#fafafa' }}>
                    <td style={{ padding:'6px 8px', fontWeight:600, color:P.text }}>
                      {r.fornecedor}
                      {r.origem==='contrato' && <span title="Gerado automaticamente pelo contrato" style={{ marginLeft:5, background:'#ede9fe', color:'#7c3aed', fontSize:'0.6rem', fontWeight:700, padding:'1px 5px', borderRadius:10 }}>Agendado</span>}
                    </td>
                    <td style={{ padding:'6px 8px', color:P.muted }}>{r.filial||'—'}</td>
                    <td style={{ padding:'4px 6px' }}><EtapaCell data={r.data_previa}    status={r.status_previa}    onChangeStatus={v=>quickField(r,'status_previa',v)} /></td>
                    <td style={{ padding:'4px 6px' }}><EtapaCell data={r.data_envio}     status={r.status_envio}     onChangeStatus={v=>quickField(r,'status_envio',v)} /></td>
                    <td style={{ padding:'4px 6px' }}><EtapaCell data={r.data_nota}      status={r.status_nota}      onChangeStatus={v=>quickField(r,'status_nota',v)} /></td>
                    <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                      <div style={{ display:'flex', gap:4 }}>
                        <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                        <GhostBtn small onClick={()=>setDelId(r.id)}>🗑</GhostBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Listagem de quem está mais próximo — sempre visível ao lado da tabela */}
        <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, padding:'14px 16px' }}>
          <div style={{ fontWeight:800, color:P.text, fontSize:'0.85rem', marginBottom:10 }}>🔥 Mais próximos</div>
          {maisProximos.length===0 && <div style={{ color:P.muted, fontSize:'0.78rem' }}>Nada pendente no momento.</div>}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {maisProximos.map(ev => (
              <div key={ev.id} style={{ borderBottom:`1px solid #f1f5f9`, paddingBottom:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontWeight:700, color:P.text, fontSize:'0.78rem' }}>{ev.fornecedor}</span>
                  <span style={{ fontWeight:800, fontSize:'0.72rem', color:prazoCor(ev.dias) }}>{prazoLabel(ev.dias)}</span>
                </div>
                <div style={{ color:P.muted, fontSize:'0.68rem' }}>{ev.tipo} · {fmtDate(ev.data)}{ev.filial ? ` · ${ev.filial}` : ''}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {modal && (
        <Modal title={edit?'Editar Nota':'Nova Nota'} onClose={close} wide>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <Field label="Fornecedor" required>
              <input value={form.fornecedor} onChange={e=>setForm(f=>({...f,fornecedor:e.target.value}))} placeholder="Nome do fornecedor" style={inp} />
            </Field>
            <Field label="Filial"><Sel value={form.filial} onChange={v=>setForm(f=>({...f,filial:v}))} options={filiais.map(f=>f.name)} placeholder="Selecionar…" /></Field>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <Field label="Data Prévia"><input type="date" value={form.data_previa} onChange={e=>setForm(f=>({...f,data_previa:e.target.value}))} style={inp} /></Field>
            <Field label="Status Prévia"><Sel value={form.status_previa} onChange={v=>setForm(f=>({...f,status_previa:v}))} options={ANDAMENTO_OPTS} /></Field>
            <Field label="Data Envio para Controle de Notas"><input type="date" value={form.data_envio} onChange={e=>setForm(f=>({...f,data_envio:e.target.value}))} style={inp} /></Field>
            <Field label="Status Envio"><Sel value={form.status_envio} onChange={v=>setForm(f=>({...f,status_envio:v}))} options={ANDAMENTO_OPTS} /></Field>
            <Field label="Vencimento"><input type="date" value={form.data_nota} onChange={e=>setForm(f=>({...f,data_nota:e.target.value}))} style={inp} /></Field>
            <Field label="Status Vencimento"><Sel value={form.status_nota} onChange={v=>setForm(f=>({...f,status_nota:v}))} options={ANDAMENTO_OPTS} /></Field>
          </div>
          <Field label="Observações">
            <textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={3} style={{ ...inp, resize:'vertical' }} />
          </Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.fornecedor.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}

      {delId && <DelModal onClose={()=>setDelId(null)} onConfirm={()=>{ deleteDoc(doc(db,col,delId)); setDelId(null); }} />}
    </div>
  );
}

// ── Aba: Contratos/Fornecedores ─────────────────────────────────────────────────
const EMPTY_CONTRATO = {
  fornecedor:'', filial:'',
  data_inicio:'', data_fim:'',
  dias_antes_previa:'5',
  envio_tipo:'dia_util', envio_dia:'5',
  vencimento_tipo:'dia_fixo', vencimento_dia:'10',
  ativo:true, observacoes:'',
};

function regraLabel(tipo, valor) {
  return tipo === 'dia_util' ? `${valor}º dia útil do mês` : `dia ${valor} do mês`;
}

/** Gera (de forma incremental — nunca duplica) uma nota mensal por mês de vigência
 *  do contrato, a partir das regras de prévia/envio/vencimento cadastradas. */
async function gerarNotasDoContrato(contrato, colNotas) {
  if (!contrato.data_inicio || !contrato.data_fim) return 0;
  const existentes = await getDocs(query(collection(db, colNotas), where('contrato_id','==',contrato.id)));
  const mesesExistentes = new Set(existentes.docs.map(d => (d.data().data_envio || '').slice(0,7)).filter(Boolean));

  const inicio = new Date(contrato.data_inicio + 'T00:00');
  const fim = new Date(contrato.data_fim + 'T00:00');
  if (isNaN(inicio) || isNaN(fim)) return 0;
  const cursor = new Date(inicio.getFullYear(), inicio.getMonth(), 1);
  const fimMes = new Date(fim.getFullYear(), fim.getMonth(), 1);

  let criadas = 0;
  while (cursor <= fimMes) {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const mesKey = `${y}-${String(m+1).padStart(2,'0')}`;
    if (!mesesExistentes.has(mesKey)) {
      const dEnvio  = calcularDataRegra(y, m, contrato.envio_tipo, contrato.envio_dia);
      const dVenc   = calcularDataRegra(y, m, contrato.vencimento_tipo, contrato.vencimento_dia);
      const dPrevia = new Date(dEnvio); dPrevia.setDate(dPrevia.getDate() - (Number(contrato.dias_antes_previa) || 0));
      await addDoc(collection(db, colNotas), {
        fornecedor: contrato.fornecedor, filial: contrato.filial, contrato_id: contrato.id, origem:'contrato',
        data_previa: dstr(dPrevia), status_previa:'Não iniciado',
        data_envio:  dstr(dEnvio),  status_envio:'Não iniciado',
        data_nota:   dstr(dVenc),   status_nota:'Não iniciado',
        data_pagamento:'', status_pagamento:'Não iniciado',
        validacao_itworks:'Pendente', observacoes:'',
        criadoEm: serverTimestamp(),
      });
      criadas++;
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return criadas;
}

function TabContratos({ contratos, colContratos, colNotas, filiais }) {
  const [modal,   setModal]   = useState(false);
  const [edit,    setEdit]    = useState(null);
  const [form,    setForm]    = useState(EMPTY_CONTRATO);
  const [saving,  setSaving]  = useState(false);
  const [delId,   setDelId]   = useState(null);
  const [gerando, setGerando] = useState(null); // id do contrato gerando agenda no momento

  const openNew  = () => { setForm(EMPTY_CONTRATO); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...EMPTY_CONTRATO, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.fornecedor.trim() || !form.data_inicio || !form.data_fim) return;
    setSaving(true);
    try {
      let contratoId = edit?.id;
      if (edit) {
        await updateDoc(doc(db,colContratos,edit.id), { ...form, updatedAt: serverTimestamp() });
      } else {
        const ref = await addDoc(collection(db,colContratos), { ...form, criadoEm: serverTimestamp() });
        contratoId = ref.id;
      }
      close();
      setGerando(contratoId);
      const criadas = await gerarNotasDoContrato({ ...form, id: contratoId }, colNotas);
      setGerando(null);
      if (criadas > 0) alert(`${criadas} nota(s) agendada(s) no calendário até o fim da vigência.`);
    } catch(e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const regenerar = async (contrato) => {
    setGerando(contrato.id);
    const criadas = await gerarNotasDoContrato(contrato, colNotas);
    setGerando(null);
    alert(criadas > 0 ? `${criadas} nota(s) nova(s) agendada(s).` : 'Nada novo pra agendar — a agenda já está completa até o fim da vigência.');
  };

  const rows = useMemo(() => [...contratos].sort((a,b) => (a.fornecedor||'').localeCompare(b.fornecedor||'')), [contratos]);

  return (
    <div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        <Btn onClick={openNew}>+ Novo Contrato</Btn>
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{rows.length} contrato{rows.length!==1?'s':''}</span>
      </div>

      <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.75rem', minWidth:1000 }}>
          <thead><tr>
            <TH>Fornecedor</TH><TH>Filial</TH><TH>Vigência</TH><TH>Regra de Envio</TH><TH>Regra de Vencimento</TH><TH>Status</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={7} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum contrato cadastrado.</td></tr>}
            {rows.map((r,i) => (
              <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9', background:i%2===0?'transparent':'#fafafa' }}>
                <td style={{ padding:'6px 8px', fontWeight:600, color:P.text }}>{r.fornecedor}</td>
                <td style={{ padding:'6px 8px', color:P.muted }}>{r.filial||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}>{fmtDate(r.data_inicio)} → {fmtDate(r.data_fim)}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{regraLabel(r.envio_tipo, r.envio_dia)}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{regraLabel(r.vencimento_tipo, r.vencimento_dia)}</td>
                <td style={{ padding:'6px 8px' }}>
                  <span style={{ background:r.ativo!==false?'#dcfce7':'#f1f5f9', color:r.ativo!==false?'#15803d':P.muted, padding:'2px 8px', borderRadius:20, fontWeight:700, fontSize:'0.68rem' }}>
                    {r.ativo!==false ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <GhostBtn small onClick={()=>regenerar(r)}>{gerando===r.id?'⏳':'🔄'}</GhostBtn>
                    <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={()=>setDelId(r.id)}>🗑</GhostBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={edit?'Editar Contrato':'Novo Contrato'} onClose={close} wide>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <Field label="Fornecedor" required><input value={form.fornecedor} onChange={e=>setForm(f=>({...f,fornecedor:e.target.value}))} placeholder="Nome do fornecedor" style={inp} /></Field>
            <Field label="Filial"><Sel value={form.filial} onChange={v=>setForm(f=>({...f,filial:v}))} options={filiais.map(f=>f.name)} placeholder="Selecionar…" /></Field>
            <Field label="Início da vigência" required><input type="date" value={form.data_inicio} onChange={e=>setForm(f=>({...f,data_inicio:e.target.value}))} style={inp} /></Field>
            <Field label="Fim da vigência" required><input type="date" value={form.data_fim} onChange={e=>setForm(f=>({...f,data_fim:e.target.value}))} style={inp} /></Field>
          </div>

          <div style={{ background:P.bg, borderRadius:10, padding:'12px 14px', margin:'6px 0 14px' }}>
            <div style={{ fontWeight:700, fontSize:'0.78rem', color:P.text, marginBottom:8 }}>Regra de envio para Controle de Notas</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
              <Field label="Tipo"><Sel value={form.envio_tipo} onChange={v=>setForm(f=>({...f,envio_tipo:v}))} options={['dia_util','dia_fixo']} /></Field>
              <Field label={form.envio_tipo==='dia_util' ? 'Nº do dia útil' : 'Dia fixo do mês'}>
                <input type="number" min="1" max="31" value={form.envio_dia} onChange={e=>setForm(f=>({...f,envio_dia:e.target.value}))} style={inp} />
              </Field>
            </div>
            <Field label="Prévia — quantos dias antes do envio"><input type="number" min="0" max="30" value={form.dias_antes_previa} onChange={e=>setForm(f=>({...f,dias_antes_previa:e.target.value}))} style={{...inp, maxWidth:160}} /></Field>
          </div>

          <div style={{ background:P.bg, borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:'0.78rem', color:P.text, marginBottom:8 }}>Regra de vencimento (pagamento)</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
              <Field label="Tipo"><Sel value={form.vencimento_tipo} onChange={v=>setForm(f=>({...f,vencimento_tipo:v}))} options={['dia_util','dia_fixo']} /></Field>
              <Field label={form.vencimento_tipo==='dia_util' ? 'Nº do dia útil' : 'Dia fixo do mês'}>
                <input type="number" min="1" max="31" value={form.vencimento_dia} onChange={e=>setForm(f=>({...f,vencimento_dia:e.target.value}))} style={inp} />
              </Field>
            </div>
          </div>

          <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, cursor:'pointer', fontSize:'0.82rem', color:P.text }}>
            <input type="checkbox" checked={form.ativo!==false} onChange={e=>setForm(f=>({...f,ativo:e.target.checked}))} />
            Contrato ativo
          </label>

          <Field label="Observações">
            <textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={3} style={{ ...inp, resize:'vertical' }} />
          </Field>

          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.fornecedor.trim()||!form.data_inicio||!form.data_fim}>{saving?'Salvando…':'Salvar e agendar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
          <p style={{ color:P.muted, fontSize:'0.72rem', marginTop:10 }}>Ao salvar, uma nota é criada automaticamente pra cada mês da vigência, com as datas de prévia/envio/vencimento já calculadas — sem duplicar meses que já existem.</p>
        </Modal>
      )}

      {delId && <DelModal onClose={()=>setDelId(null)} onConfirm={()=>{ deleteDoc(doc(db,colContratos,delId)); setDelId(null); }} />}
    </div>
  );
}

// ── Aba: Calendário ──────────────────────────────────────────────────────────────
function TabCalendario({ notas, filiais }) {
  const [view,      setView]      = useState('calendario'); // 'calendario' | 'lista'
  const hoje = new Date();
  const [ano,        setAno]        = useState(hoje.getFullYear());
  const [mes,        setMes]        = useState(hoje.getMonth()); // 0-based
  const [filFilial,  setFilFilial]  = useState('');
  const [diaSel,     setDiaSel]     = useState(null);

  const eventos = useMemo(() => {
    const out = [];
    notas.forEach(r => {
      if (filFilial && r.filial !== filFilial) return;
      if (r.data_previa) out.push({ id:r.id+'_p', fornecedor:r.fornecedor, filial:r.filial, tipo:'Prévia', data:r.data_previa, status:r.status_previa, cor:'#8b5cf6' });
      if (r.data_envio)  out.push({ id:r.id+'_e', fornecedor:r.fornecedor, filial:r.filial, tipo:'Envio',  data:r.data_envio,  status:r.status_envio,  cor:'#3b82f6' });
      if (r.data_nota)   out.push({ id:r.id+'_v', fornecedor:r.fornecedor, filial:r.filial, tipo:'Vencimento', data:r.data_nota, status:r.status_nota, cor:'#ef4444' });
    });
    return out;
  }, [notas, filFilial]);

  const eventosPorDia = useMemo(() => {
    const map = {};
    eventos.forEach(ev => { (map[ev.data] ||= []).push(ev); });
    return map;
  }, [eventos]);

  const eventosDoMes = useMemo(() => {
    const prefixo = `${ano}-${String(mes+1).padStart(2,'0')}`;
    return eventos.filter(ev => ev.data.startsWith(prefixo)).sort((a,b) => a.data.localeCompare(b.data));
  }, [eventos, ano, mes]);

  const eventosLista = useMemo(() => [...eventos].sort((a,b) => a.data.localeCompare(b.data)), [eventos]);

  const mudarMes = delta => {
    let m = mes + delta, y = ano;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    setMes(m); setAno(y); setDiaSel(null);
  };

  const primeiroDiaSemana = new Date(ano, mes, 1).getDay();
  const diasNoMes = new Date(ano, mes+1, 0).getDate();
  const celulas = [];
  for (let i=0;i<primeiroDiaSemana;i++) celulas.push(null);
  for (let d=1; d<=diasNoMes; d++) celulas.push(d);

  const hojeStr = dstr(new Date());

  return (
    <div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        <div style={{ display:'flex', gap:0, background:'#f1f5f9', borderRadius:10, padding:3, width:'fit-content' }}>
          {[{k:'calendario',l:'🗓️ Calendário'},{k:'lista',l:'📋 Lista'}].map(t => (
            <button key={t.k} onClick={()=>setView(t.k)} style={{
              border:'none', borderRadius:8, padding:'6px 16px', cursor:'pointer', fontSize:'0.8rem', fontWeight:700,
              background: view===t.k ? P.white : 'transparent', color: view===t.k ? P.primary : P.muted,
              boxShadow: view===t.k ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
            }}>{t.l}</button>
          ))}
        </div>
        <select value={filFilial} onChange={e=>setFilFilial(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Todas as filiais</option>
          {filiais.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
        </select>
        <div style={{ display:'flex', gap:4, marginLeft:4 }}>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.7rem', color:P.muted }}><i style={{width:8,height:8,borderRadius:8,background:'#8b5cf6',display:'inline-block'}} />Prévia</span>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.7rem', color:P.muted, marginLeft:8 }}><i style={{width:8,height:8,borderRadius:8,background:'#3b82f6',display:'inline-block'}} />Envio</span>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.7rem', color:P.muted, marginLeft:8 }}><i style={{width:8,height:8,borderRadius:8,background:'#ef4444',display:'inline-block'}} />Vencimento</span>
        </div>
      </div>

      {view === 'calendario' && (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
            <GhostBtn small onClick={()=>mudarMes(-1)}>← Anterior</GhostBtn>
            <div style={{ fontWeight:800, color:P.text, fontSize:'0.95rem', minWidth:160, textAlign:'center' }}>{MESES_PT[mes]} / {ano}</div>
            <GhostBtn small onClick={()=>mudarMes(1)}>Próximo →</GhostBtn>
          </div>

          <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflow:'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
              {DIA_SEMANA_PT.map(d => (
                <div key={d} style={{ padding:'8px 4px', textAlign:'center', fontSize:'0.68rem', fontWeight:700, color:P.muted, textTransform:'uppercase', borderBottom:`1px solid ${P.border}` }}>{d}</div>
              ))}
              {celulas.map((d,i) => {
                if (d === null) return <div key={'e'+i} style={{ minHeight:78, borderRight:`1px solid #f1f5f9`, borderBottom:`1px solid #f1f5f9`, background:'#fafbfc' }} />;
                const dataStr = `${ano}-${String(mes+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                const evs = eventosPorDia[dataStr] || [];
                const isHoje = dataStr === hojeStr;
                return (
                  <div key={d} onClick={()=>evs.length && setDiaSel(diaSel===dataStr?null:dataStr)}
                    style={{ minHeight:78, padding:'5px 6px', borderRight:`1px solid #f1f5f9`, borderBottom:`1px solid #f1f5f9`,
                      cursor: evs.length ? 'pointer' : 'default', background: diaSel===dataStr ? '#eef2ff' : P.white }}>
                    <div style={{ fontSize:'0.72rem', fontWeight: isHoje?800:600, color: isHoje?P.primary:P.text,
                      width:20, height:20, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                      background: isHoje?P.primary:'transparent', ...(isHoje && {color:'#fff'}) }}>{d}</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:2, marginTop:4 }}>
                      {evs.slice(0,4).map(ev => <i key={ev.id} title={`${ev.tipo} · ${ev.fornecedor}`} style={{ width:7, height:7, borderRadius:7, background:ev.cor, display:'inline-block' }} />)}
                      {evs.length > 4 && <span style={{ fontSize:'0.6rem', color:P.muted }}>+{evs.length-4}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {diaSel && (
            <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, padding:'14px 16px', marginTop:12 }}>
              <div style={{ fontWeight:800, color:P.text, fontSize:'0.85rem', marginBottom:8 }}>{fmtDate(diaSel)}</div>
              {(eventosPorDia[diaSel]||[]).map(ev => (
                <div key={ev.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid #f1f5f9' }}>
                  <i style={{ width:9, height:9, borderRadius:9, background:ev.cor, flex:'none' }} />
                  <span style={{ fontWeight:700, color:P.text, fontSize:'0.78rem' }}>{ev.fornecedor}</span>
                  <span style={{ color:P.muted, fontSize:'0.72rem' }}>{ev.tipo}{ev.filial?` · ${ev.filial}`:''}</span>
                  <span style={{ marginLeft:'auto', fontSize:'0.68rem', fontWeight:700, color:ANDAMENTO_COLOR[ev.status||'Não iniciado'] }}>{ev.status||'Não iniciado'}</span>
                </div>
              ))}
            </div>
          )}

          {eventosDoMes.length === 0 && <p style={{ color:P.muted, fontSize:'0.8rem', marginTop:12 }}>Nenhum evento nesse mês.</p>}
        </>
      )}

      {view === 'lista' && (
        <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflowX:'auto' }}>
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.75rem', minWidth:760 }}>
            <thead><tr>
              <TH>Data</TH><TH>Tipo</TH><TH>Fornecedor</TH><TH>Filial</TH><TH>Status</TH><TH>Prazo</TH>
            </tr></thead>
            <tbody>
              {eventosLista.length===0 && <tr><td colSpan={6} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum evento.</td></tr>}
              {eventosLista.map(ev => {
                const dias = diasAte(ev.data);
                return (
                  <tr key={ev.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}>{fmtDate(ev.data)}</td>
                    <td style={{ padding:'6px 8px' }}><span style={{ background:`${ev.cor}18`, color:ev.cor, padding:'2px 8px', borderRadius:20, fontWeight:700, fontSize:'0.68rem' }}>{ev.tipo}</span></td>
                    <td style={{ padding:'6px 8px', fontWeight:600, color:P.text }}>{ev.fornecedor}</td>
                    <td style={{ padding:'6px 8px', color:P.muted }}>{ev.filial||'—'}</td>
                    <td style={{ padding:'6px 8px', color:ANDAMENTO_COLOR[ev.status||'Não iniciado'], fontWeight:700 }}>{ev.status||'Não iniciado'}</td>
                    <td style={{ padding:'6px 8px', fontWeight:700, color:prazoCor(dias) }}>{prazoLabel(dias)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Aba 2: Controle de KM ────────────────────────────────────────────────────────
const EMPTY_KM = {
  motorista:'', placa:'', modelo_carro:'',
  km_inicial:'', km_final:'', litragem:'',
  valor_combustivel:'', valor_abastecido:'', data_abastecimento:'',
  observacoes:'',
};

function kmRodado(r) {
  const ini = parseFloat(r.km_inicial), fim = parseFloat(r.km_final);
  if (isNaN(ini) || isNaN(fim) || fim < ini) return null;
  return fim - ini;
}

function TabKm({ data, col }) {
  const [search, setSearch] = useState('');
  const [modal,  setModal]  = useState(false);
  const [edit,   setEdit]   = useState(null);
  const [form,   setForm]   = useState(EMPTY_KM);
  const [saving, setSaving] = useState(false);
  const [delId,  setDelId]  = useState(null);

  const openNew  = () => { setForm(EMPTY_KM); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...EMPTY_KM, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const save = async () => {
    if (!form.motorista.trim() || !form.placa.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db,col,edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db,col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const rows = useMemo(() => {
    let r = data;
    if (search) r = r.filter(x => ((x.motorista||'')+(x.placa||'')).toLowerCase().includes(search.toLowerCase()));
    return [...r].sort((a,b) => (toSortDate(b.data_abastecimento)||'').localeCompare(toSortDate(a.data_abastecimento)||''));
  }, [data, search]);

  const kpis = useMemo(() => ({
    total:           data.length,
    litros:          data.reduce((acc,x)=>acc+(parseFloat(x.litragem)||0),0),
    valorAbastecido: data.reduce((acc,x)=>acc+(parseFloat(x.valor_abastecido)||0),0),
    kmTotal:         data.reduce((acc,x)=>acc+(kmRodado(x)||0),0),
  }), [data]);

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <KpiCard label="Total de abastecimentos" value={kpis.total}                                     color={P.muted} />
        <KpiCard label="Litros abastecidos"       value={kpis.litros.toLocaleString('pt-BR')}            color={P.blue}  />
        <KpiCard label="Valor total abastecido"   value={fmtMoeda(kpis.valorAbastecido)}                 color={P.green} />
        <KpiCard label="KM total rodado"          value={kpis.kmTotal.toLocaleString('pt-BR')}           color={P.amber} />
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        <Btn onClick={openNew}>+ Novo Abastecimento</Btn>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar motorista ou placa…"
          style={{ ...inp, maxWidth:260, flex:1 }} />
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{rows.length} registro{rows.length!==1?'s':''}</span>
      </div>

      <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.75rem', minWidth:1000 }}>
          <thead><tr>
            <TH>Motorista</TH><TH>Placa</TH><TH>Modelo</TH><TH>KM Inicial</TH><TH>KM Final</TH><TH>KM Rodado</TH>
            <TH>Litragem</TH><TH>Valor Combustível</TH><TH>Valor Abastecido</TH><TH>Data</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={11} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {rows.map((r,i) => (
              <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9', background:i%2===0?'transparent':'#fafafa' }}>
                <td style={{ padding:'6px 8px', fontWeight:600, color:P.text }}>{r.motorista}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{r.placa}</td>
                <td style={{ padding:'6px 8px', color:P.muted }}>{r.modelo_carro||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{r.km_inicial||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{r.km_final||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.text, fontWeight:700 }}>{kmRodado(r) ?? '—'}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{r.litragem||'—'}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{r.valor_combustivel ? fmtMoeda(r.valor_combustivel) : '—'}</td>
                <td style={{ padding:'6px 8px', color:P.text }}>{r.valor_abastecido ? fmtMoeda(r.valor_abastecido) : '—'}</td>
                <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}>{fmtDate(r.data_abastecimento)}</td>
                <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={()=>setDelId(r.id)}>🗑</GhostBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={edit?'Editar Abastecimento':'Novo Abastecimento'} onClose={close} wide>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <Field label="Motorista" required><input value={form.motorista} onChange={e=>setForm(f=>({...f,motorista:e.target.value}))} placeholder="Nome do motorista" style={inp} /></Field>
            <Field label="Placa" required><input value={form.placa} onChange={e=>setForm(f=>({...f,placa:e.target.value.toUpperCase()}))} placeholder="Ex.: ABC1D23" style={inp} /></Field>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Modelo do carro"><input value={form.modelo_carro} onChange={e=>setForm(f=>({...f,modelo_carro:e.target.value}))} placeholder="Ex.: Fiat Argo" style={inp} /></Field>
            </div>
            <Field label="KM Inicial"><input type="number" value={form.km_inicial} onChange={e=>setForm(f=>({...f,km_inicial:e.target.value}))} style={inp} /></Field>
            <Field label="KM Final"><input type="number" value={form.km_final} onChange={e=>setForm(f=>({...f,km_final:e.target.value}))} style={inp} /></Field>
            <Field label="Litragem (L)"><input type="number" step="0.01" value={form.litragem} onChange={e=>setForm(f=>({...f,litragem:e.target.value}))} style={inp} /></Field>
            <Field label="Data do abastecimento"><input type="date" value={form.data_abastecimento} onChange={e=>setForm(f=>({...f,data_abastecimento:e.target.value}))} style={inp} /></Field>
            <Field label="Valor Combustível (R$)"><input type="number" step="0.01" value={form.valor_combustivel} onChange={e=>setForm(f=>({...f,valor_combustivel:e.target.value}))} style={inp} /></Field>
            <Field label="Valor Abastecido (R$)"><input type="number" step="0.01" value={form.valor_abastecido} onChange={e=>setForm(f=>({...f,valor_abastecido:e.target.value}))} style={inp} /></Field>
          </div>
          <Field label="Observações">
            <textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={3} style={{ ...inp, resize:'vertical' }} />
          </Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.motorista.trim()||!form.placa.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}

      {delId && <DelModal onClose={()=>setDelId(null)} onConfirm={()=>{ deleteDoc(doc(db,col,delId)); setDelId(null); }} />}
    </div>
  );
}

// ── Aba 3: Controle de Multas ──────────────────────────────────────────────────────
const EMPTY_MULTA = {
  motorista:'', placa:'', data_infracao:'', valor_multa:'',
  prazo_infrator:'', motivo_infracao:'', num_infracao:'', observacoes:'',
  indicacao_real_infrator:'',
};

function statusPrazo(prazo) {
  if (!prazo) return { label:'Sem prazo', color:P.muted, bg:'#f1f5f9' };
  const diff = Math.ceil((new Date(prazo+'T00:00') - new Date()) / 86400000);
  if (diff < 0)  return { label:`Atrasado ${Math.abs(diff)}d`, color:P.red,   bg:'#fee2e2' };
  if (diff <= 3) return { label:`Em ${diff}d`,                  color:P.amber, bg:'#fef9c3' };
  return           { label:`Em ${diff}d`,                        color:P.green, bg:'#dcfce7' };
}

function TabMultas({ data, col }) {
  const [search, setSearch] = useState('');
  const [modal,  setModal]  = useState(false);
  const [edit,   setEdit]   = useState(null);
  const [form,   setForm]   = useState(EMPTY_MULTA);
  const [saving, setSaving] = useState(false);
  const [delId,  setDelId]  = useState(null);

  const openNew  = () => { setForm(EMPTY_MULTA); setEdit(null); setModal(true); };
  const openEdit = r  => { setForm({ ...EMPTY_MULTA, ...r }); setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const salvarIndicacao = async (id, valor) => {
    try { await updateDoc(doc(db,col,id), { indicacao_real_infrator: valor, updatedAt: serverTimestamp() }); }
    catch(e) { alert('Erro ao salvar: ' + e.message); }
  };

  const save = async () => {
    if (!form.motorista.trim() || !form.placa.trim()) return;
    setSaving(true);
    try {
      if (edit) await updateDoc(doc(db,col,edit.id), { ...form, updatedAt: serverTimestamp() });
      else      await addDoc(collection(db,col), { ...form, criadoEm: serverTimestamp() });
      close();
    } catch(e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const rows = useMemo(() => {
    let r = data;
    if (search) r = r.filter(x => ((x.motorista||'')+(x.placa||'')+(x.num_infracao||'')).toLowerCase().includes(search.toLowerCase()));
    return [...r].sort((a,b) => (toSortDate(b.data_infracao)||'').localeCompare(toSortDate(a.data_infracao)||''));
  }, [data, search]);

  const kpis = useMemo(() => ({
    total:      data.length,
    valorTotal: data.reduce((acc,x)=>acc+(parseFloat(x.valor_multa)||0),0),
    atrasadas:  data.filter(x => x.prazo_infrator && statusPrazo(x.prazo_infrator).label.startsWith('Atrasado')).length,
    proximas:   data.filter(x => {
      if (!x.prazo_infrator) return false;
      const diff = Math.ceil((new Date(x.prazo_infrator+'T00:00') - new Date()) / 86400000);
      return diff >= 0 && diff <= 3;
    }).length,
  }), [data]);

  return (
    <div>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <KpiCard label="Total de multas"           value={kpis.total}               color={P.muted} />
        <KpiCard label="Valor total"                value={fmtMoeda(kpis.valorTotal)} color={P.blue}  />
        <KpiCard label="Prazos atrasados"           value={kpis.atrasadas}           color={P.red}   />
        <KpiCard label="Prazos próx. 3 dias"        value={kpis.proximas}            color={P.amber} />
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        <Btn onClick={openNew}>+ Nova Multa</Btn>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar motorista, placa ou nº da infração…"
          style={{ ...inp, maxWidth:300, flex:1 }} />
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{rows.length} registro{rows.length!==1?'s':''}</span>
      </div>

      <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.75rem', minWidth:960 }}>
          <thead><tr>
            <TH>Motorista</TH><TH>Placa</TH><TH>Data Infração</TH><TH>Valor Multa</TH>
            <TH>Prazo Indicação Infrator</TH><TH>Indicação Real Infrator</TH><TH>Motivo</TH><TH>Nº Infração</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={9} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {rows.map((r,i) => {
              const sp = statusPrazo(r.prazo_infrator);
              return (
                <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9', background:i%2===0?'transparent':'#fafafa' }}>
                  <td style={{ padding:'6px 8px', fontWeight:600, color:P.text }}>{r.motorista}</td>
                  <td style={{ padding:'6px 8px', color:P.text }}>{r.placa}</td>
                  <td style={{ padding:'6px 8px', color:P.text, whiteSpace:'nowrap' }}>{fmtDate(r.data_infracao)}</td>
                  <td style={{ padding:'6px 8px', color:P.text }}>{r.valor_multa ? fmtMoeda(r.valor_multa) : '—'}</td>
                  <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                    <div style={{ color:P.text, fontSize:'0.72rem' }}>{fmtDate(r.prazo_infrator)}</div>
                    <span style={{ background:sp.bg, color:sp.color, padding:'1px 7px', borderRadius:20,
                      fontWeight:700, fontSize:'0.65rem', whiteSpace:'nowrap' }}>{sp.label}</span>
                  </td>
                  <td style={{ padding:'6px 8px' }} onClick={e=>e.stopPropagation()}>
                    <select
                      value={r.indicacao_real_infrator || ''}
                      onChange={e=>salvarIndicacao(r.id, e.target.value)}
                      style={{
                        border:'none', borderRadius:20, padding:'2px 8px', fontWeight:700, fontSize:'0.68rem', cursor:'pointer', outline:'none',
                        background: r.indicacao_real_infrator==='sim' ? '#dcfce7' : r.indicacao_real_infrator==='nao' ? '#fee2e2' : '#f1f5f9',
                        color: r.indicacao_real_infrator==='sim' ? P.green : r.indicacao_real_infrator==='nao' ? P.red : P.muted,
                      }}>
                      <option value="">—</option>
                      <option value="sim">Sim</option>
                      <option value="nao">Não</option>
                    </select>
                  </td>
                  <td style={{ padding:'6px 8px', color:P.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:160 }} title={r.motivo_infracao}>{r.motivo_infracao||'—'}</td>
                  <td style={{ padding:'6px 8px', color:P.muted }}>{r.num_infracao||'—'}</td>
                  <td style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <GhostBtn small onClick={()=>openEdit(r)}>✏️</GhostBtn>
                      <GhostBtn small onClick={()=>setDelId(r.id)}>🗑</GhostBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={edit?'Editar Multa':'Nova Multa'} onClose={close} wide>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
            <Field label="Motorista" required><input value={form.motorista} onChange={e=>setForm(f=>({...f,motorista:e.target.value}))} placeholder="Nome do motorista" style={inp} /></Field>
            <Field label="Placa" required><input value={form.placa} onChange={e=>setForm(f=>({...f,placa:e.target.value.toUpperCase()}))} placeholder="Ex.: ABC1D23" style={inp} /></Field>
            <Field label="Data da infração"><input type="date" value={form.data_infracao} onChange={e=>setForm(f=>({...f,data_infracao:e.target.value}))} style={inp} /></Field>
            <Field label="Valor da multa (R$)"><input type="number" step="0.01" value={form.valor_multa} onChange={e=>setForm(f=>({...f,valor_multa:e.target.value}))} style={inp} /></Field>
            <Field label="Prazo p/ indicação do infrator"><input type="date" value={form.prazo_infrator} onChange={e=>setForm(f=>({...f,prazo_infrator:e.target.value}))} style={inp} /></Field>
            <Field label="Nº da infração"><input value={form.num_infracao} onChange={e=>setForm(f=>({...f,num_infracao:e.target.value}))} style={inp} /></Field>
            <div style={{ gridColumn:'1/-1' }}>
              <Field label="Motivo da infração"><input value={form.motivo_infracao} onChange={e=>setForm(f=>({...f,motivo_infracao:e.target.value}))} placeholder="Ex.: Avanço de sinal" style={inp} /></Field>
            </div>
          </div>
          <Field label="Observações">
            <textarea value={form.observacoes} onChange={e=>setForm(f=>({...f,observacoes:e.target.value}))} rows={3} style={{ ...inp, resize:'vertical' }} />
          </Field>
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Btn onClick={save} disabled={saving||!form.motorista.trim()||!form.placa.trim()}>{saving?'Salvando…':'Salvar'}</Btn>
            <GhostBtn onClick={close}>Cancelar</GhostBtn>
          </div>
        </Modal>
      )}

      {delId && <DelModal onClose={()=>setDelId(null)} onConfirm={()=>{ deleteDoc(doc(db,col,delId)); setDelId(null); }} />}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GestaoControladoria() {
  const [tab, setTab] = useState('notas');
  const [data, setData] = useState({ notas:[], km:[], multas:[], contratos:[] });
  const [filiais, setFiliais] = useState([]);

  const colNotas     = getCollectionName('controladoria_notas');
  const colKm        = getCollectionName('controladoria_km');
  const colMultas    = getCollectionName('controladoria_multas');
  const colContratos = getCollectionName('controladoria_contratos');

  useEffect(() => {
    const unsubs = [];
    const loadCol = (col, key) => {
      const q = query(collection(db, col), orderBy('criadoEm','desc'));
      unsubs.push(onSnapshot(q, snap =>
        setData(prev => ({ ...prev, [key]: snap.docs.map(d => ({ id:d.id, ...d.data() })) }))
      ));
    };
    loadCol(colNotas,     'notas');
    loadCol(colKm,        'km');
    loadCol(colMultas,    'multas');
    loadCol(colContratos, 'contratos');
    unsubs.push(onSnapshot(query(collection(db, getCollectionName('units_config')), orderBy('name')),
      snap => setFiliais(snap.docs.map(d => ({ id:d.id, ...d.data() })))));
    return () => unsubs.forEach(u => u());
  }, [colNotas, colKm, colMultas, colContratos]);

  const tabStyle = k => ({
    padding:'8px 16px', borderRadius:'8px 8px 0 0',
    border:`1px solid ${P.border}`, borderBottom: k===tab ? `2px solid ${P.primary}` : `1px solid ${P.border}`,
    background: k===tab ? P.white : '#f1f5f9',
    color: k===tab ? P.primary : P.muted,
    fontWeight: k===tab ? 800 : 600, fontSize:'0.8rem', cursor:'pointer',
    position:'relative', top:'1px', whiteSpace:'nowrap',
  });

  return (
    <div style={{ background:P.bg, minHeight:'100vh', padding:'24px 24px 60px' }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ margin:0, fontSize:'1.4rem', fontWeight:800, color:P.text }}>🧾 Controle de Notas</h1>
        <p style={{ margin:'4px 0 0', color:P.muted, fontSize:'0.83rem' }}>Pagamento de notas, controle de KM/abastecimento e multas da frota homecare</p>
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:4, borderBottom:`1px solid ${P.border}`, marginBottom:0 }}>
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={tabStyle(t.k)}>{t.l}</button>
        ))}
      </div>

      <div style={{ background:P.white, border:`1px solid ${P.border}`, borderTop:'none', borderRadius:'0 0 12px 12px', padding:'22px 20px' }}>
        {tab==='notas'      && <TabNotas      data={data.notas} col={colNotas} filiais={filiais} />}
        {tab==='emails'     && <RecebidosEmail />}
        {tab==='contratos'  && <TabContratos  contratos={data.contratos} colContratos={colContratos} colNotas={colNotas} filiais={filiais} />}
        {tab==='calendario' && <TabCalendario notas={data.notas} filiais={filiais} />}
        {tab==='km'         && <TabKm     data={data.km}     col={colKm} />}
        {tab==='multas'     && <TabMultas data={data.multas} col={colMultas} />}
      </div>
    </div>
  );
}
